/**
 * Errors that carry the *data* a component delete was blocked on.
 *
 * The previous design encoded this as a string sentinel — `ASSEMBLY_USAGE:` glued
 * to a Hebrew sentence, thrown by the context and re-read by the grid, which then
 * printed the whole thing (prefix included) into a failure toast. Two things were
 * wrong with it and only one of them was cosmetic: the caller could not recover the
 * assembly names to *ask* about them, so the question "האם להמשיך?" reached the user
 * with no button to press, and the delete simply failed.
 *
 * A typed error carries the list. Discrimination is on `name`, not `instanceof`:
 * the project targets ES2020 and subclassing Error survives that, but `name` costs
 * nothing and does not depend on the prototype chain surviving a bundler.
 */

export interface BlockingAssembly {
  id: string;
  name: string;
}

/** A component the user asked to delete is still referenced by assemblies. */
export class ComponentInUseError extends Error {
  readonly assemblies: BlockingAssembly[];

  constructor(assemblies: BlockingAssembly[]) {
    super(`רכיב זה נמצא בשימוש ב-${assemblies.length} הרכבות`);
    this.name = 'ComponentInUseError';
    this.assemblies = assemblies;
  }
}

/**
 * The component was deleted, but the assemblies that referenced it could not be
 * flagged incomplete.
 *
 * This is not a failed delete and must never be reported as one. The row is gone
 * from the database; what is missing is the flag that tells anyone reading those
 * assemblies that they now cost less than they did an hour ago. Silence here would
 * leave an assembly reading "complete" while a component has been removed from
 * under it — the exact state `is_complete` exists to make visible.
 */
export class AssembliesNotFlaggedError extends Error {
  readonly assemblies: BlockingAssembly[];
  /** Declared rather than using `Error.cause`, which needs lib ES2022. */
  readonly cause: unknown;

  constructor(assemblies: BlockingAssembly[], cause: unknown) {
    super(
      `הרכיב נמחק, אך ${assemblies.length} הרכבות לא סומנו כלא שלמות: ${assemblies
        .map(a => a.name)
        .join(', ')}`
    );
    this.name = 'AssembliesNotFlaggedError';
    this.assemblies = assemblies;
    this.cause = cause;
  }
}

function hasAssemblies(err: unknown, name: string): boolean {
  return (
    err instanceof Error &&
    err.name === name &&
    Array.isArray((err as { assemblies?: unknown }).assemblies)
  );
}

export function isComponentInUseError(
  err: unknown
): err is ComponentInUseError {
  return hasAssemblies(err, 'ComponentInUseError');
}

export function isAssembliesNotFlaggedError(
  err: unknown
): err is AssembliesNotFlaggedError {
  return hasAssemblies(err, 'AssembliesNotFlaggedError');
}

/**
 * Every distinct assembly across a set of blocked components, deduplicated by id.
 *
 * A bulk delete of eight components blocked by the same two assemblies must ask
 * about two assemblies, not sixteen.
 */
export function uniqueAssemblies(
  blocked: Array<{ assemblies: BlockingAssembly[] }>
): BlockingAssembly[] {
  const seen = new Map<string, BlockingAssembly>();
  for (const item of blocked) {
    for (const assembly of item.assemblies) {
      if (!seen.has(assembly.id)) seen.set(assembly.id, assembly);
    }
  }
  return Array.from(seen.values());
}
